import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import PostAckDiscountModal from "@/components/PostAckDiscountModal";

const RESEARCHER_OPTIONS = [
  { value: "independent",     label: "Independent Research" },
  { value: "academic",        label: "Academic / University Research" },
  { value: "pharmaceutical",  label: "Pharmaceutical Research" },
  { value: "clinical",        label: "Clinical Laboratory" },
  { value: "veterinary",      label: "Veterinary Research" },
  { value: "other",           label: "Other Scientific Research" },
];

const ACK_ITEMS = [
  { key: "ack_age",              text: "I am 21 years of age or older." },
  { key: "ack_research_only",    text: "I understand all products offered by Bulk Buy Kits are sold strictly for laboratory research use only." },
  { key: "ack_no_human_animal",  text: "I will NOT administer, consume, ingest, inject, or apply these products to any human being or animal." },
  { key: "ack_not_fda_approved", text: "I understand these products are NOT approved by the FDA and have not been evaluated for safety or efficacy in humans." },
  { key: "ack_no_resale",        text: "I will not resell, redistribute, transfer, or gift these products to any third party." },
];

export default function ComplianceGate() {
  const { user, refresh } = useAuth();
  const [researcherType, setResearcherType] = useState("");
  const [signedName, setSignedName] = useState(user?.name || "");
  const [acks, setAcks] = useState(Object.fromEntries(ACK_ITEMS.map((i) => [i.key, false])));
  const [legalBindingAck, setLegalBindingAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);

  const allAcked = ACK_ITEMS.every((i) => acks[i.key]);
  const canSubmit = researcherType && signedName.trim().length > 0 && allAcked && legalBindingAck;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/compliance", {
        researcher_type: researcherType,
        signed_name: signedName.trim(),
        ...acks,
      });
      toast.success("Acknowledgment recorded — welcome.");
      // iter_140: show the 10% off discount modal BEFORE dismissing the
      // gate. `refresh()` is deferred until the modal closes so the gate
      // stays behind us and the buyer lands on /shop right after.
      // iter182 — suppress the 10% modal entirely if the buyer is on the
      // Supply side. Supply is wholesale-priced; the discount is Select-only.
      const onSupply = (() => {
        try {
          const p = typeof window !== "undefined" ? window.location.pathname : "";
          if (p.startsWith("/supply") || p.startsWith("/preview/supply-tiers")) return true;
          return localStorage.getItem("bbk_last_tier") === "supply";
        } catch { return false; }
      })();
      if (onSupply) {
        await refresh();
      } else {
        await refresh(); // Welcome discount offer disabled by owner.
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to record acknowledgment");
    } finally {
      setBusy(false);
    }
  };

  const handleDiscountClose = async () => {
    setShowDiscount(false);
    await refresh();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-charcoal/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="compliance-gate">
      <div className="bg-bone rounded-2xl max-w-2xl w-full my-8 shadow-2xl">
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-borderSubtle">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 bg-oxblood/10 rounded-full flex items-center justify-center shrink-0">
              <ShieldAlert size={20} strokeWidth={1.75} className="text-oxblood"/>
            </div>
            <div>
              <p className="text-[13px] uppercase tracking-[0.22em] text-charcoal/85 mb-1">Required · One-time</p>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-none">Research Use Acknowledgment</h2>
              <p className="text-[15px] text-charcoal/90 mt-3 leading-relaxed">
                Before you can view pricing or place an order, we require every member to acknowledge the terms below.
                This applies to all products on Bulk Buy Kits.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="px-8 py-6 space-y-6">
          {/* Researcher type — prominent so it doesn't get skimmed past */}
          <div className="border border-charcoal/25 rounded-lg bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[13px] uppercase tracking-[0.22em] text-charcoal font-medium">
                Which best describes your work?
              </Label>
              <span className="text-[10px] uppercase tracking-[0.24em] font-mono px-2 py-0.5 bg-oxblood/10 text-oxblood rounded-sm">
                Required
              </span>
            </div>
            <p className="text-[13px] text-charcoal/85 mb-3">Pick one — it helps us route your account.</p>
            <Select value={researcherType} onValueChange={setResearcherType}>
              <SelectTrigger
                className="h-12 rounded-md border border-charcoal/40 bg-bone px-4 focus:ring-0 focus:border-charcoal text-[15px]"
                data-testid="researcher-type"
              >
                <SelectValue placeholder="Select your researcher type…" />
              </SelectTrigger>
              <SelectContent className="!z-[200]" position="popper">
                {RESEARCHER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`researcher-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Acknowledgments */}
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-charcoal/85">Acknowledgments</Label>
            <p className="text-[14px] text-charcoal/90 mt-1.5 mb-3">Tap each checkbox to confirm.</p>
            <div className="space-y-3">
              {ACK_ITEMS.map((item) => {
                const checked = acks[item.key];
                return (
                  <label
                    key={item.key}
                    className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer border transition-colors ${
                      checked
                        ? "bg-white border-charcoal"
                        : "bg-white/60 border-borderSubtle hover:border-charcoal hover:bg-white"
                    }`}
                    data-testid={`ack-row-${item.key}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => setAcks((a) => ({ ...a, [item.key]: !!v }))}
                      className="mt-0.5 h-6 w-6 border-2 border-charcoal bg-white rounded shrink-0 data-[state=checked]:bg-charcoal data-[state=checked]:text-bone data-[state=checked]:border-charcoal"
                      data-testid={`ack-${item.key}`}
                    />
                    <span className="text-sm text-charcoal leading-snug select-none">{item.text}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Signature */}
          <div>
            <Label htmlFor="signed_name" className="text-[13px] uppercase tracking-[0.22em] text-charcoal/85">
              Digital signature — type your full legal name
            </Label>
            <Input
              id="signed_name"
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              className="mt-2 h-11 rounded-none border-0 border-b border-charcoal/30 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-charcoal font-serif text-xl italic"
              placeholder="Your full name"
              data-testid="signed-name"
            />
            <p className="text-[13px] text-charcoal/85 mt-1.5">By typing your name, you are electronically signing this acknowledgment.</p>
          </div>

          {/* Legal binding acknowledgment — requires its own explicit check */}
          <label
            className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer border transition-colors ${
              legalBindingAck ? "bg-white border-charcoal" : "bg-white/60 border-borderSubtle hover:border-charcoal hover:bg-white"
            }`}
            data-testid="legal-binding-row"
          >
            <Checkbox
              checked={legalBindingAck}
              onCheckedChange={(v) => setLegalBindingAck(!!v)}
              className="mt-0.5 h-6 w-6 border-2 border-charcoal bg-white rounded shrink-0 data-[state=checked]:bg-charcoal data-[state=checked]:text-bone data-[state=checked]:border-charcoal"
              data-testid="ack-legal-binding"
            />
            <span className="text-[14px] text-charcoal/90 leading-relaxed select-none">
              <span className="text-charcoal font-semibold">I acknowledge and accept the following legally binding terms:</span>{" "}
              This acknowledgment is legally binding. Bulk Buy Kits reserves the right to refuse service or cancel any order
              that does not comply with these terms. My acknowledgment, the timestamp, and my IP address are recorded
              for compliance purposes. Bulk Buy Kits does not sell to residents of any jurisdiction where the sale of research
              chemicals is prohibited.
            </span>
          </label>

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit || busy}
            className="w-full py-4 bg-charcoal text-bone tracking-[0.22em] text-xs uppercase hover:bg-oxblood transition-colors rounded-full disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-charcoal"
            data-testid="compliance-submit"
          >
            {busy
              ? "Recording…"
              : canSubmit
              ? "I agree — record my acknowledgment"
              : "Complete all fields to continue"}
          </button>
        </form>
      </div>
      <PostAckDiscountModal open={showDiscount} onClose={handleDiscountClose} />
    </div>
  );
}
